/* eslint-disable no-nested-ternary */
import { BuildingOffice2Icon, UserCircleIcon } from "@heroicons/react/20/solid";
import { Select, Text } from "@mantine/core";
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  memo,
} from "react";
import FilterRow from "./FilterRow";
import { determineEntityFromField, getAllFieldsForEntity } from "./utils/entityUtils";

type Props = {
  onRemove: (currentKey: number, filterId?: number) => void;
  currentKey: number;
  workspaceSchema: any;
  filterId: number;
  onCompleteFilter: (
    field: any,
    operator: any,
    value: any,
    type: any,
    entityType: any,
    id: number,
    filterId: number,
  ) => void;
  filter: any;
  entityRelation?: "accounts" | "users";
};

const typeChoices = [
  { value: "accounts", label: "Accounts", Icon: BuildingOffice2Icon },
  { value: "users", label: "Users", Icon: UserCircleIcon },
];

const RowEntitySelect = memo(({
  onRemove,
  filter,
  workspaceSchema,
  currentKey,
  onCompleteFilter,
  filterId,
  entityRelation,
}: Props) => {
  // Entity state - only needed for user manual selection (cross-entity filters)
  const [localEntity, setLocalEntity] = useState<"accounts" | "users" | null>(null);
  
  // Memoize derived entity to avoid recalculation
  const derivedEntity = useMemo(
    () => determineEntityFromField(filter?.field, entityRelation, workspaceSchema),
    [filter?.field, entityRelation, workspaceSchema]
  );
  
  // Compute current entity: use local if set, otherwise derive from filter/entityRelation
  const currentEntity = localEntity || derivedEntity;
  
  // Get all fields (fields + metrics merged) matching Edit Columns data source
  const allFields: any = useMemo(() => {
    if (!currentEntity) return { list: [], obj: {} };
    return getAllFieldsForEntity(currentEntity, workspaceSchema);
  }, [currentEntity, workspaceSchema]);

  // Memoize allColumns for field lookup
  const allColumns = useMemo(
    () => allFields?.obj || {},
    [allFields?.obj]
  );

  // Split into traits and success metrics (same as AccountControls)
  const filteredTraits = useMemo(
    () => allFields?.list?.filter((item: any) =>
      !item.exclude_display
      && item?.attribute_type !== "metric_traits"
      && item?.generation_type !== "computed_trait"
    ) || [],
    [allFields?.list]
  );

  const filteredSuccessMetrics = useMemo(
    () => allFields?.list?.filter((item: any) =>
      !item.exclude_display && item?.generation_type === "computed_trait"
    ) || [],
    [allFields?.list]
  );

  // Reset local entity when filter field or entityRelation changes significantly
  // Only reset if the derived entity matches entityRelation (meaning user hasn't intentionally overridden)
  useEffect(() => {
    if (!filter?.field && entityRelation) {
      // If filter is cleared, reset to entityRelation
      setLocalEntity(null);
      return;
    }
    
    // If derived entity matches entityRelation, it means the field naturally belongs to that entity
    // So we can reset local override if it exists
    if (derivedEntity === entityRelation && localEntity !== null) {
      setLocalEntity(null);
    }
  }, [filter?.field, entityRelation, derivedEntity, localEntity]);

  const handleEntityChange = useCallback((value: string | null) => {
    if (value === "accounts" || value === "users") {
      setLocalEntity(value);
    }
  }, []);

  // Memoize initField to prevent unnecessary FilterRow re-renders
  const initField = useMemo(() => {
    if (!filter?.field || !allColumns[filter.field]) {
      return undefined;
    }
    
    return {
      name: allColumns[filter.field]?.name,
      display_name: allColumns[filter.field]?.display_name,
      id: 5000,
      attribute_type: allColumns[filter.field]?.attribute_type,
      entity_type: filter?.entity_type,
      data_type: allColumns[filter.field]?.data_type,
      generation_type: allColumns[filter.field]?.generation_type,
    };
  }, [filter?.field, filter?.entity_type, allColumns]);

  // Create stable key for FilterRow
  const filterRowKey = useMemo(
    () => `${currentEntity}-${filterId}-${currentKey}-${filter?.field || 'empty'}`,
    [currentEntity, filterId, currentKey, filter?.field]
  );

  return (
    <div className="flex gap-2 items-center">
      <Text
        c="text.5"
        mr={5}
        ml={currentKey === 0 ? 0 : 15}
        size="sm"
      >
        {currentKey === 0 ? "Where" : "And"}
      </Text>
      <Select 
        size="sm" 
        value={currentEntity} 
        data={typeChoices} 
        onChange={handleEntityChange} 
      />
      <FilterRow
        entity={currentEntity}
        key={filterRowKey}
        initField={initField}
        initOps={filter?.operator}
        initValues={filter?.value}
        onRemove={onRemove}
        traits={filteredTraits}
        successMetrics={filteredSuccessMetrics}
        currentKey={currentKey}
        filterId={filterId}
        onCompleteFilter={onCompleteFilter}
      />
    </div>
  );
});

RowEntitySelect.displayName = "RowEntitySelect";

export default RowEntitySelect;
